import React, { useEffect, useRef, useState } from 'react';
import { aboutContent, images } from '../mockData';

const About = () => {
  const [visibleSections, setVisibleSections] = useState([]);
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observers = [];
    
    sectionRefs.current.forEach((ref, index) => {
      if (ref) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setVisibleSections((prev) => [...new Set([...prev, index])]);
              }
            });
          },
          { threshold: 0.2 }
        );
        
        observer.observe(ref);
        observers.push(observer);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        height: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: 'var(--bg-primary)'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${images.hero.darkAbstract})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.3
        }} />
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), rgba(0,0,0,0.95))'
        }} />
        
        <div style={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          padding: '0 7.6923%'
        }}>
          <div style={{
            width: '80px',
            height: '2px',
            background: 'var(--brand-primary)',
            margin: '0 auto 30px'
          }} />
          <h1 className="display-large">About Promesa Trading</h1>
          <p className="body-large" style={{
            maxWidth: '700px',
            margin: '20px auto 0',
            color: 'var(--text-secondary)'
          }}>
            Over three decades of excellence in international trade, built on Swiss precision and global expertise since 1989.
          </p>
        </div>
      </section>

      {/* Content Sections */}
      <section style={{
        padding: '100px 7.6923%',
        background: 'var(--bg-primary)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {aboutContent.sections.map((section, index) => (
            <div
              key={index}
              ref={(el) => (sectionRefs.current[index] = el)}
              style={{
                marginBottom: '100px',
                opacity: visibleSections.includes(index) ? 1 : 0,
                transform: visibleSections.includes(index) ? 'translateY(0)' : 'translateY(30px)',
                transition: 'all 0.8s ease-out'
              }}
            >
              {/* Section with alternating layout */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: index % 2 === 0 ? '1fr 2fr' : '2fr 1fr',
                gap: '60px',
                alignItems: 'center'
              }}>
                {index % 2 === 0 ? (
                  <>
                    <div>
                      <div style={{
                        width: '60px',
                        height: '2px',
                        background: 'var(--brand-primary)',
                        marginBottom: '24px'
                      }} />
                      <h2 className="display-medium" style={{
                        marginBottom: '24px'
                      }}>
                        {section.title}
                      </h2>
                    </div>
                    <div>
                      <p className="body-medium" style={{
                        lineHeight: '1.8',
                        color: 'var(--text-secondary)'
                      }}>
                        {section.content}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="body-medium" style={{
                        lineHeight: '1.8',
                        color: 'var(--text-secondary)'
                      }}>
                        {section.content}
                      </p>
                    </div>
                    <div>
                      <div style={{
                        width: '60px',
                        height: '2px',
                        background: 'var(--brand-primary)',
                        marginBottom: '24px'
                      }} />
                      <h2 className="display-medium" style={{
                        marginBottom: '24px'
                      }}>
                        {section.title}
                      </h2>
                    </div>
                  </>
                )}
              </div>

              {/* Divider */}
              {index < aboutContent.sections.length - 1 && (
                <div style={{
                  marginTop: '60px',
                  height: '1px',
                  background: 'var(--border-subtle)'
                }} />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Image Section */}
      <section style={{
        padding: '0 7.6923% 100px',
        background: 'var(--bg-primary)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          height: '500px',
          overflow: 'hidden'
        }}>
          <img 
            src={images.hero.luxuryWatch} 
            alt="Swiss Luxury" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to right, rgba(0,0,0,0.92), rgba(0,0,0,0.65))',
            display: 'flex',
            alignItems: 'center',
            padding: '60px'
          }}>
            <div style={{
              maxWidth: '500px'
            }}>
              <h3 className="display-medium" style={{
                marginBottom: '20px',
                color: 'var(--text-primary)'
              }}>
                Swiss Precision,
                <br />
                <span style={{ color: 'var(--brand-primary)' }}>Global Impact</span>
              </h3>
              <p className="body-medium" style={{
                color: 'var(--text-secondary)'
              }}>
                Registered and operating from the heart of Switzerland, we bring Swiss values of precision, reliability, and excellence to every international trade partnership.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;